import React, {useCallback, useReducer, useState} from 'react';
import {PromptButton} from 'shared/PromptButton';

import type {TableButton, TableOptionType, TableRowType} from 'shared/types';
import DropdownOption from './DropdownOption';
import styled from 'styled-components';

type MobileOptionsType = {
    colspan: number;
    options: TableOptionType;
    row: TableRowType;
    isOptionsMenuOpen: boolean;
    className: string;
};

const MobileOptions = ({
    colspan,
    options,
    row,
    isOptionsMenuOpen,
    className,
}: MobileOptionsType): JSX.Element => {
    const [hideButtons, setHideButtons] = useState(false);
    const [icons, setIcons] = useReducer(
        (state: string[], action: {index: number; payload: string}) => {
            if (typeof state[action.index] !== 'undefined') {
                const updatedState = [...state];

                updatedState[action.index] = action.payload;

                return updatedState;
            }

            return state;
        },
        [],
        () => {
            if (options.hasOwnProperty('buttons')) {
                return options.buttons.map((button) => button.iconName);
            }

            return [];
        }
    );

    const handleButtonAction = useCallback(
        (button: TableButton, index: number) =>
            (event: React.MouseEvent<HTMLElement>) => {
                button.action(row, event, icons[Number(index)], (icon) => {
                    setIcons({
                        index,
                        payload: icon,
                    });
                });
            },
        [row]
    );

    if (isOptionsMenuOpen) {
        return (
            <tr className={`mobile-menu-table ${className}`}>
                <td colSpan={colspan}>
                    <MobileMenuWrapper>
                        {options.buttons.map((button, index) => {
                            if (
                                typeof button.isHidden !== 'undefined' &&
                                button.isHidden(row)
                            )
                                return <i key={button.title} />;

                            if (
                                button.hasOwnProperty('dropdown') &&
                                button.dropdown.length
                            ) {
                                if (!hideButtons) {
                                    return (
                                        <DropdownOption
                                            row={row}
                                            key={button.title}
                                            button={button}
                                        />
                                    );
                                }
                            } else if (button.as) {
                                const Component = button.as;
                                return (
                                    <Component
                                        row={row}
                                        key={button.title}
                                        setHideButtons={setHideButtons}
                                    />
                                );
                            } else {
                                if (!hideButtons) {
                                    return (
                                        <PromptButton
                                            key={button.title}
                                            title={button.title}
                                            iconName={icons[Number(index)]}
                                            action={handleButtonAction(
                                                button,
                                                index
                                            )}
                                            showConfirmation={
                                                button.confirmChanges
                                            }
                                            className={
                                                options.buttons.length === 1
                                                    ? 'mobile-lone-button'
                                                    : ''
                                            }
                                        />
                                    );
                                }
                            }
                        })}
                    </MobileMenuWrapper>
                </td>
            </tr>
        );
    }
};

const MobileMenuWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export default MobileOptions;
